import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2 } from 'lucide-react';
import { roleApi } from '../../../services/api';
import { Role } from '../../../types';
import LoadingCard from '../../../components/LoadingCard';

interface RoleDetailsPanelProps {
  roleId: number;
  onClose: () => void;
  onUpdate: () => void;
}

const RoleDetailsPanel: React.FC<RoleDetailsPanelProps> = ({ roleId, onClose, onUpdate }) => {
  const [role, setRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRole = async () => {
      try {
        setLoading(true);
        const data = await roleApi.getById(roleId);
        setRole(data);
      } catch (error) {
        console.error('Failed to load role:', error);
      } finally {
        setLoading(false);
      }
    };
    loadRole();
  }, [roleId]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await roleApi.delete(roleId);
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      alert(error?.response?.data?.message || 'Failed to delete role');
    }
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
        <div className="max-w-md w-full mx-4">
          <LoadingCard title="Role Details" message="Loading role details..." />
        </div>
      </div>,
      document.body
    );
  }

  if (!role) {
    return null;
  }

  // Group permissions by module
  let permissionsByModule: Record<string, Array<{ id: number; name: string; action: string }>> = {};
  
  if (role.permissions_by_module) {
    permissionsByModule = role.permissions_by_module;
  } else if (role.permissions) {
    role.permissions.forEach((perm) => {
      const parts = perm.name?.split('.') || [];
      const module = parts[0] || 'other';
      if (!permissionsByModule[module]) {
        permissionsByModule[module] = [];
      }
      permissionsByModule[module].push({
        id: perm.id,
        name: perm.name || '',
        action: parts[1] || 'unknown',
      });
    });
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">Role Details</h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium theme-text-secondary mb-0.5">Role Name</label>
            <p className="text-base font-semibold theme-text-primary">{role.name}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-0.5">Permissions</label>
              <p className="text-base theme-text-primary">{role.permissions_count || 0}</p>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-0.5">Assigned Users</label>
              <p className="text-base theme-text-primary">{role.users_count || 0}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-1">Permissions by Module</label>
            <p className="text-sm theme-text-secondary mb-2">
              Permissions are organized by module.
            </p>
            {Object.keys(permissionsByModule).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(permissionsByModule).map(([module, perms]) => (
                  <div key={module} className="theme-border rounded-lg p-3 theme-card">
                    <h4 className="text-sm font-semibold theme-text-primary mb-2 capitalize">
                      {module.replace('_', ' ')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {perms.map((perm) => (
                        <span
                          key={perm.id}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full capitalize"
                        >
                          {perm.action}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="theme-text-tertiary text-center py-8 text-sm">No permissions assigned</p>
            )}
          </div>

          <div className="flex justify-between gap-2 pt-3">
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Role
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm theme-border rounded theme-bg-secondary theme-text-primary hover:opacity-80"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RoleDetailsPanel;

